'use client'

import StandingsCsvExport from '@/app/manage/(community)/[communityId]/racing/[championshipId]/results/components/standings-csv-export'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { Button } from '@/components/ui/button'
import { isColorLight } from '@/helpers/color-utils'
import { getResultsVisibleEvents } from '@/helpers/event-results'
import useDriverDisplayIdentifier from '@/hooks/use-driver-display-identifier'
import { zodResolver } from '@hookform/resolvers/zod'
import Link from 'next/link'
import { useEffect, useMemo } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import useSWRImmutable from 'swr/immutable'
import { z } from 'zod'
import StandingsDriverTable from './driver/standings-driver-table'
import StandingsFilterForm from './standings-filter-form'
import StandingsTeamTable from './team/standings-team-table'

async function fetchStandings({
  championshipId,
  eventId,
  sessionId,
  splitIds = [],
  tableType,
  includeUnclassified = false,
}) {
  const searchParams = new URLSearchParams()

  if (eventId) {
    searchParams.set('eventId', eventId)
  }

  if (sessionId) {
    searchParams.set('sessionId', sessionId)
  }

  if (tableType) {
    searchParams.set('tableType', tableType)
  }

  if (includeUnclassified) {
    searchParams.set('includeUnclassified', 'true')
  }

  splitIds.forEach((splitId) => {
    if (splitId) {
      searchParams.append('splitIds', splitId)
    }
  })

  const response = await fetch(
    `/api/internal/championship/${championshipId}/standings?${searchParams.toString()}`,
    {
      cache: 'no-store',
    },
  )

  if (!response.ok) {
    const error = new Error('Failed to load standings')
    error.status = response.status
    throw error
  }

  return response.json()
}

export default function StandingsSection({
  championship,
  events,
  selectedSplits,
  setSelectedSplits,
  presetEventId,
  isManagerSide,
}) {
  const visibleEvents = useMemo(
    () =>
      getResultsVisibleEvents(events, {
        includeUnpublished: Boolean(isManagerSide),
      }),
    [events, isManagerSide],
  )

  const filtersForm = useForm({
    resolver: zodResolver(
      z.object({
        eventId: z.string().nullable(),
        sessionId: z.string().nullable(),
        userId: z.string().nullable(),
        tableType: z.enum(['team', 'driver']),
        carClassId: z.string(),
      }),
    ),
    defaultValues: {
      eventId: presetEventId,
      tableType: 'driver',
      carClassId: 'all',
    },
  })

  const [
    selectedEventID,
    selectedSessionID,
    selectedUserId,
    tableType,
    selectedCarClassId,
  ] = useWatch({
    control: filtersForm.control,
    name: ['eventId', 'sessionId', 'userId', 'tableType', 'carClassId'],
  })

  // Force team view if championship is team only
  useEffect(() => {
    if (championship.isTeamOnly) {
      filtersForm.setValue('tableType', 'team')
    }
  }, [championship.isTeamOnly, filtersForm])

  const { data: results, isLoading } = useSWRImmutable(
    {
      championshipId: championship.id,
      eventId: selectedEventID || null,
      splitIds: selectedSplits || [],
      sessionId: selectedSessionID,
      tableType,
      includeUnclassified: tableType === 'driver',
    },
    fetchStandings,
  )

  const selectedEvent = useMemo(() => {
    if (!selectedEventID) return null
    return visibleEvents.find((e) => e.id === selectedEventID)
  }, [selectedEventID, visibleEvents])

  const selectedEventSessionSequence = useMemo(() => {
    if (!Array.isArray(events) || !selectedEventID) return []

    return events.find((event) => event.id === selectedEventID)?.sessions ?? []
  }, [events, selectedEventID])

  const selectedSession = useMemo(() => {
    if (!selectedEvent || !selectedSessionID) return null
    return selectedEvent.sessions.find((s) => s.id === selectedSessionID)
  }, [selectedEvent, selectedSessionID])

  useEffect(() => {
    if (!selectedEvent) {
      filtersForm.setValue('sessionId', null)
      return
    }

    filtersForm.setValue('sessionId', selectedEvent.sessions.at(-1)?.id ?? null)
  }, [filtersForm, selectedEvent])

  useEffect(() => {
    if (!selectedUserId) return

    const rows = document.getElementsByClassName(
      `standings-user-${selectedUserId}`,
    )

    if (rows.length === 0) return

    const headerOffset = 130
    const elementPosition = rows[0].getBoundingClientRect().top
    const offsetPosition = elementPosition + window.scrollY - headerOffset

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth',
    })
  }, [selectedUserId])

  useEffect(() => {
    filtersForm.setValue('userId', undefined)
  }, [filtersForm, selectedEventID, selectedSessionID, tableType])

  const handleUserClicked = (user) => {
    filtersForm.setValue('tableType', 'driver')
    filtersForm.setValue('carClassId', 'all')
    setTimeout(() => filtersForm.setValue('userId', user.id), 100)
  }

  const displayIdentifierSource =
    championship.gameName ||
    championship.game?.name ||
    championship.game?.nicename ||
    ''

  const {
    driverDisplayIdentifier,
    driverDisplayIdentifierOptions,
    setDriverDisplayIdentifier,
  } = useDriverDisplayIdentifier({
    items: Array.isArray(results) ? results : [],
    source: displayIdentifierSource,
    context: 'standings',
    defaultIdentifier: 'gf_name',
  })

  const standingsUrl = isManagerSide
    ? `/manage/${championship.communityId}/racing/${championship.id}/results`
    : `/c/${championship.community.slug}/${championship.id}?tabName=Standings`

  return (
    <div className="mt-5 flex flex-col gap-5">
      <StandingsFilterForm
        championship={championship}
        events={visibleEvents}
        selectedEvent={selectedEvent}
        selectedEventSessionSequence={selectedEventSessionSequence}
        presetEventId={presetEventId}
        tableType={tableType}
        form={filtersForm}
        isManagerSide={isManagerSide}
        selectedSplits={selectedSplits}
        setSelectedSplits={setSelectedSplits}
        driverDisplayIdentifier={driverDisplayIdentifier}
        driverDisplayIdentifierOptions={driverDisplayIdentifierOptions}
        setDriverDisplayIdentifier={setDriverDisplayIdentifier}
        filterAction={
          isManagerSide ? (
            <StandingsCsvExport
              championship={championship}
              eventId={selectedEventID}
              compact
            />
          ) : null
        }
      />

      <div className="divide"></div>

      {championship.splits.length > 0 ? (
        selectedSplits.length > 0 ? (
          <Accordion
            type="multiple"
            collapsible="true"
            defaultValue={championship.splits.map((s) => s.id)}
          >
            {championship.splits
              .filter((split) => selectedSplits.includes(split.id))
              .map((split) => (
                <AccordionItem
                  key={split.id}
                  value={split.id}
                  className="mb-10 border-b-0"
                >
                  <AccordionTrigger>
                    <div
                      className="rounded-full px-4 py-2 text-p-1 font-semibold"
                      style={{
                        backgroundColor: split.color ?? '#ffffff',
                        color: isColorLight(split.color ?? '#ffffff')
                          ? '#000000'
                          : '#ffffff',
                      }}
                    >
                      {split.name}
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    {selectedSession &&
                    !selectedSession.splits.some(
                      (s) => s.championshipSplitId === split.id,
                    ) ? (
                      <p>This split is not available for this session.</p>
                    ) : (
                      <div className="max-h-lvh overflow-y-auto px-2">
                        {tableType === 'driver' ? (
                          <StandingsDriverTable
                            championship={championship}
                            session={selectedSession}
                            splitId={split.id}
                            results={results}
                            isLoading={isLoading}
                            selectedUserId={selectedUserId}
                            driverDisplayIdentifier={driverDisplayIdentifier}
                            selectedCarClassId={selectedCarClassId}
                            includeUnclassified
                          />
                        ) : (
                          <StandingsTeamTable
                            championship={championship}
                            splitId={split.id}
                            results={results}
                            isLoading={isLoading}
                            handleUserClicked={handleUserClicked}
                            driverDisplayIdentifier={driverDisplayIdentifier}
                          />
                        )}
                      </div>
                    )}
                  </AccordionContent>
                </AccordionItem>
              ))}
          </Accordion>
        ) : (
          <div className="flex flex-col items-center justify-center gap-5">
            <h6 className="text-h-6 font-semibold">
              No splits selected. Please select splits to view standings.
            </h6>

            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setSelectedSplits(championship.splits.map((s) => s.id))
              }}
            >
              Reset Splits
            </Button>
          </div>
        )
      ) : (
        <div className="max-h-lvh overflow-y-auto px-2">
          {tableType === 'driver' ? (
            <StandingsDriverTable
              championship={championship}
              session={selectedSession}
              results={results}
              isLoading={isLoading}
              selectedUserId={selectedUserId}
              driverDisplayIdentifier={driverDisplayIdentifier}
              selectedCarClassId={selectedCarClassId}
              includeUnclassified
            />
          ) : (
            <StandingsTeamTable
              championship={championship}
              results={results}
              isLoading={isLoading}
              handleUserClicked={handleUserClicked}
              driverDisplayIdentifier={driverDisplayIdentifier}
            />
          )}
        </div>
      )}

      {selectedEventID && !isLoading && !championship.isSingleEvent && (
        <>
          {presetEventId && !isManagerSide ? (
            <Button className="w-full" variant="outline" asChild>
              <Link href={standingsUrl}>View Championship Standings</Link>
            </Button>
          ) : (
            <Button
              className="w-full"
              variant="outline"
              onClick={() => {
                filtersForm.reset({
                  eventId: presetEventId,
                  tableType: 'driver',
                  carClassId: 'all',
                })
                document.getElementById('standings-form')?.scrollIntoView()
              }}
            >
              View Championship Standings
            </Button>
          )}
        </>
      )}
    </div>
  )
}
